import { Box, Chip, Stack } from "@mui/joy";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { DataNodeConstructorMode } from "features/dataNodes";
import { DataNodeConstructorParamsWrapper } from "features/dataNodes/components/DataNodeConstructor/DataNodeConstructorParamsWrapper";
import { TableNodeColumnConstructor } from "features/dataNodes/components/DataNodeConstructor/TableNodeColumnConstructor";
import {
  useMediaDataRoom,
  useMediaDataRoomInsightsData,
} from "features/mediaDataRoom/contexts";
import { getAdvertiserAudienceColumns } from "features/mediaDataRoom/models";
import { type DataRoomTableColumn } from "models";
import AdvertiserDataReportLabel from "../AdvertiserDataReportLabel/AdvertiserDataReportLabel";
import DataNodeItem from "../DataNodeItem";
import AdvertiserValidationReportDialog from "./AdvertiserValidationReportDialog";
import SeedAudienceDataNodeActions from "./SeedAudienceDataNodeActions";

const SeedAudienceDataNode = memo(() => {
  const {
    matchingIdFormat,
    hashMatchingIdWith,
    seedAudienceProviderName,
    supportedFeatures: { canViewSeedAudienceDetails },
  } = useMediaDataRoom();
  const {
    publishedDatasetsHashes: { advertiserDatasetHash: datasetHash },
    advertiserDataReport,
  } = useMediaDataRoomInsightsData();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isValidationReportOpen, setIsValidationReportOpen] = useState(false);
  const toggleCollapse = useCallback(
    () => setIsCollapsed((state) => !state),
    [setIsCollapsed]
  );
  const columns = useMemo<DataRoomTableColumn[]>(
    () => getAdvertiserAudienceColumns(matchingIdFormat, hashMatchingIdWith),
    [matchingIdFormat, hashMatchingIdWith]
  );
  return (
    <Fragment>
      <DataNodeItem
        actions={
          <SeedAudienceDataNodeActions
            columns={columns}
            datasetHash={datasetHash}
            openValidationReport={setIsValidationReportOpen}
          />
        }
        content={
          canViewSeedAudienceDetails && (
            <DataNodeConstructorParamsWrapper
              mode={DataNodeConstructorMode.ACTION}
            >
              <Box sx={{ padding: (theme) => theme.spacing(1, 0) }}>
                <TableNodeColumnConstructor
                  columns={columns}
                  columnsOrder={columns.map(({ id }) => id)}
                  isLoading={false}
                  tableNodeId={"audience_leaf"}
                />
              </Box>
            </DataNodeConstructorParamsWrapper>
          )
        }
        description={
          datasetHash && canViewSeedAudienceDetails ? (
            <AdvertiserDataReportLabel />
          ) : null
        }
        isCollapsed={isCollapsed || !canViewSeedAudienceDetails}
        title={
          <Stack direction="row" gap={1}>
            <span>Seed audience</span>
            {seedAudienceProviderName && (
              <Chip>{seedAudienceProviderName}</Chip>
            )}
          </Stack>
        }
        toggleCollapse={canViewSeedAudienceDetails ? toggleCollapse : undefined}
      />
      <AdvertiserValidationReportDialog
        data={advertiserDataReport.computeResults}
        loading={advertiserDataReport.loading}
        onClose={() => setIsValidationReportOpen(false)}
        open={isValidationReportOpen}
      />
    </Fragment>
  );
});

SeedAudienceDataNode.displayName = "SeedAudienceDataNodeActions";

export default SeedAudienceDataNode;
